/**
 * /dashboard/settings/fleetplus — Kafaratplus ("Fleet Plus") fuel integration.
 *
 * Their diesel is pulled over their API and staged. A staged operation only
 * becomes a fuel transaction once somebody says which hub vehicle it belongs
 * to, so this page is mostly about answering that question once per truck.
 *
 * None of their plates existed here when this was built, so "map" usually
 * means "create this truck", which is why that is a first-class button rather
 * than something to go and do elsewhere first.
 */
import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Plug, RefreshCw, Loader2, Truck, Check, AlertTriangle, Plus, X,
} from 'lucide-react';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

interface FleetPlusVehicle {
  id: string; externalId: string;
  plate: string | null; name: string | null;
  brand: string | null; model: string | null; vehicleType: string | null;
  fuelType: string | null; branch: string | null; status: string | null;
  mappedVehicleId: string | null; mappedPlate: string | null; mappedCode: string | null;
  mappedHow: 'auto' | 'manual' | null;
  operations: number; waiting: number; liters: number; sar: number;
  firstAt: string | null; lastAt: string | null;
}

interface Status {
  configured: boolean;
  lastRun: Record<string, unknown> | null;
  counts: {
    vehicles: number; mapped: number; unmapped: number;
    operations: number; promoted: number; waiting: number;
    waitingLiters: number; waitingSar: number;
  };
}

interface HubVehicle { id: string; plateEn: string; code: string }

const sar = (n: number) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const num = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 1 });

/** "1835BEA" -> "1835-BEA", the shape hub plates are stored in. */
function hubPlateFrom(raw: string | null): string {
  const s = (raw ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const m = /^(\d{3,4})([A-Z]{2,3})$/.exec(s);
  return m ? `${m[1]}-${m[2]}` : (raw ?? '');
}

export default function FleetPlusSettingsPage() {
  const qc = useQueryClient();
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [onlyUnmapped, setOnlyUnmapped] = useState(true);

  const { data: status } = useQuery({
    queryKey: ['fleetplus-status'],
    queryFn: async () => (await apiClient.get<Status>('/api/v1/fleetplus/status')).data,
    refetchInterval: 30_000,
  });

  const { data: vehicles, isLoading } = useQuery({
    queryKey: ['fleetplus-vehicles', onlyUnmapped],
    queryFn: async () =>
      (await apiClient.get<{ items: FleetPlusVehicle[] }>(
        `/api/v1/fleetplus/vehicles${onlyUnmapped ? '?only=unmapped' : ''}`,
      )).data.items,
  });

  // The hub's own trucks, for the "map to an existing one" dropdown.
  const { data: hubVehicles } = useQuery({
    queryKey: ['fleetplus-hub-vehicles'],
    queryFn: async () =>
      (await apiClient.get<{ items: HubVehicle[] }>('/api/v1/vehicles?pageSize=500')).data.items ?? [],
  });

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ['fleetplus-status'] });
    qc.invalidateQueries({ queryKey: ['fleetplus-vehicles'] });
    qc.invalidateQueries({ queryKey: ['fleetplus-hub-vehicles'] });
  };

  const syncMut = useMutation({
    mutationFn: async () => apiClient.post('/api/v1/fleetplus/sync', {}),
    onSuccess: (r) => {
      const d = r.data as Record<string, number>;
      setErr(null);
      setMsg(`Pulled ${d.vehiclesSeen} vehicles and ${d.operationsSeen} operations · ${d.operationsNew} new · ${d.promoted} added to fuel.`);
      refreshAll();
    },
    onError: (e) => { setMsg(null); setErr(extractErrorMessage(e)); },
  });

  const mapMut = useMutation({
    mutationFn: async ({ id, vehicleId }: { id: string; vehicleId: string | null }) =>
      apiClient.put(`/api/v1/fleetplus/vehicles/${id}/mapping`, { vehicleId }),
    onSuccess: (r) => {
      const promoted = (r.data as { promoted?: number })?.promoted ?? 0;
      setErr(null);
      setMsg(promoted > 0 ? `Linked · ${promoted} fuel record(s) moved into the vehicle's history.` : 'Linked.');
      refreshAll();
    },
    onError: (e) => { setMsg(null); setErr(extractErrorMessage(e)); },
  });

  // Create the hub truck and link it in one call. The server builds the vehicle
  // from what Fleet Plus already told us - including the Arabic plate, which
  // only exists in their raw payload - so this page never has to assemble a
  // create-vehicle body or know which fields are mandatory.
  const [creatingId, setCreatingId] = useState<string | null>(null);
  const [newPlate, setNewPlate] = useState('');
  const [newType, setNewType] = useState('other');
  const createMut = useMutation({
    mutationFn: async (v: FleetPlusVehicle) => {
      const plate = (newPlate.trim() || hubPlateFrom(v.plate)).toUpperCase();
      const m = /^(\d{3,4})-([A-Z]{2,3})$/.exec(plate);
      if (!m) throw new Error('Plate must look like 1835-BEA.');
      return apiClient.post(`/api/v1/fleetplus/vehicles/${v.id}/create-hub-vehicle`, {
        plateDigits: m[1], plateLettersEn: m[2], vehicleType: newType,
      });
    },
    onSuccess: (r) => {
      const d = r.data as { plateEn?: string; code?: string; promoted?: number };
      setCreatingId(null); setNewPlate(''); setErr(null);
      setMsg(`${d.plateEn} created as ${d.code} and linked · ${d.promoted ?? 0} fuel record(s) moved into its history.`);
      refreshAll();
    },
    onError: (e) => {
      setMsg(null);
      setErr(e instanceof Error && e.message.startsWith('Plate must') ? e.message : extractErrorMessage(e));
    },
  });

  const rows = vehicles ?? [];
  const totals = useMemo(() => ({
    sar: rows.reduce((a, r) => a + r.sar, 0),
    liters: rows.reduce((a, r) => a + r.liters, 0),
  }), [rows]);

  const c = status?.counts;

  return (
    <div className="min-h-screen w-full bg-background flex flex-col">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="font-display text-lg inline-flex items-center gap-2">
            <Plug className="h-5 w-5 text-orange-500" /> Fleet Plus integration
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="gold" className="gap-1"
              disabled={syncMut.isPending || status?.configured === false}
              onClick={() => syncMut.mutate()}>
              {syncMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Sync now
            </Button>
            <Link to="/dashboard/settings">
              <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Settings</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-8 space-y-5">
        {status && !status.configured && (
          <p className="text-sm text-destructive rounded border border-destructive/30 bg-destructive/10 px-3 py-2" role="alert">
            No credentials are configured on the server, so nothing can be pulled.
          </p>
        )}
        {msg && <p className="text-sm text-emerald-600 rounded border border-emerald-500/30 bg-emerald-500/10 px-3 py-2">{msg}</p>}
        {err && <p className="text-sm text-destructive rounded border border-destructive/30 bg-destructive/10 px-3 py-2" role="alert">{err}</p>}

        {/* What is here, and what is still stuck behind a missing link. */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Their vehicles', value: c ? `${c.mapped} / ${c.vehicles}` : '—', hint: 'linked to a truck here' },
            { label: 'Fuel records', value: c ? String(c.operations) : '—', hint: `${c?.promoted ?? 0} already counted` },
            { label: 'Waiting to link', value: c ? String(c.waiting) : '—', hint: 'not in your fuel figures yet', tone: (c?.waiting ?? 0) > 0 ? 'amber' : '' },
            { label: 'Value waiting', value: c ? sar(c.waitingSar) : '—', hint: `${num(c?.waitingLiters ?? 0)} litres`, tone: (c?.waitingSar ?? 0) > 0 ? 'amber' : '' },
          ].map((k) => (
            <div key={k.label} className={`rounded-lg border p-3 ${k.tone === 'amber' ? 'border-amber-500/40 bg-amber-500/5' : 'border-border bg-card'}`}>
              <div className="text-xs uppercase tracking-wider text-muted-foreground">{k.label}</div>
              <div className="text-xl font-display mt-1 font-mono">{k.value}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{k.hint}</div>
            </div>
          ))}
        </section>

        {(c?.waiting ?? 0) > 0 && (
          <p className="text-xs text-amber-700 rounded border border-amber-500/30 bg-amber-500/5 px-3 py-2 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-px" />
            Fuel sits here until its truck is linked. Until then it is not in the fuel dashboard,
            not in consumption, and not on any driver&apos;s salary sheet.
          </p>
        )}

        <div className="flex items-center justify-between gap-2">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            Their vehicles {onlyUnmapped ? '· not yet linked' : '· all'}
          </div>
          <Button size="sm" variant="ghost" onClick={() => setOnlyUnmapped((v) => !v)}>
            {onlyUnmapped ? 'Show all' : 'Show only unlinked'}
          </Button>
        </div>

        <div className="rounded-xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground">
              <tr>
                <th className="text-start font-medium px-4 py-2">Fleet Plus vehicle</th>
                <th className="text-end font-medium px-4 py-2">Fuel records</th>
                <th className="text-end font-medium px-4 py-2">Litres</th>
                <th className="text-end font-medium px-4 py-2">SAR</th>
                <th className="text-start font-medium px-4 py-2">Linked to</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline me-2" /> Loading…</td></tr>}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center text-muted-foreground">
                  {onlyUnmapped ? 'Every Fleet Plus vehicle is linked.' : 'Nothing pulled yet — press Sync now.'}
                </td></tr>
              )}
              {rows.map((v) => (
                <tr key={v.id} className="border-t border-border align-top">
                  <td className="px-4 py-2">
                    <div className="font-mono">{v.plate ?? '—'}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {[v.brand, v.model, v.branch].filter(Boolean).join(' · ') || '—'}
                    </div>
                  </td>
                  <td className="px-4 py-2 text-end font-mono">
                    {v.operations}
                    {v.waiting > 0 && <div className="text-[11px] text-amber-600">{v.waiting} waiting</div>}
                  </td>
                  <td className="px-4 py-2 text-end font-mono">{num(v.liters)}</td>
                  <td className="px-4 py-2 text-end font-mono">{sar(v.sar)}</td>
                  <td className="px-4 py-2">
                    {v.mappedVehicleId ? (
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 text-emerald-700">
                          <Check className="h-3.5 w-3.5" /> <span className="font-mono">{v.mappedPlate}</span>
                        </span>
                        <span className="text-[11px] text-muted-foreground">{v.mappedCode} · {v.mappedHow}</span>
                        <button type="button" className="text-[11px] underline text-muted-foreground hover:text-destructive"
                          onClick={() => mapMut.mutate({ id: v.id, vehicleId: null })}>
                          unlink
                        </button>
                      </div>
                    ) : creatingId === v.id ? (
                      <div className="flex items-end gap-2 flex-wrap">
                        <div>
                          <label className="text-[11px] text-muted-foreground" htmlFor={`p-${v.id}`}>New plate</label>
                          <Input id={`p-${v.id}`} className="h-9 w-32 font-mono" dir="ltr"
                            value={newPlate} onChange={(e) => setNewPlate(e.target.value.toUpperCase())} />
                        </div>
                        <div>
                          <label className="text-[11px] text-muted-foreground" htmlFor={`t-${v.id}`}>Type</label>
                          <select id={`t-${v.id}`}
                            className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                            value={newType} onChange={(e) => setNewType(e.target.value)}>
                            {['other', 'container_truck', 'flatbed', 'water_tanker', 'sewage_tanker', 'dumper', 'pickup', 'bus']
                              .map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
                          </select>
                        </div>
                        <Button size="sm" variant="gold" disabled={createMut.isPending}
                          onClick={() => createMut.mutate(v)} className="gap-1">
                          {createMut.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Create &amp; link
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setCreatingId(null); setNewPlate(''); }}>
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          className="h-9 rounded-md border border-input bg-background px-2 text-sm max-w-48"
                          value=""
                          disabled={mapMut.isPending}
                          onChange={(e) => { if (e.target.value) mapMut.mutate({ id: v.id, vehicleId: e.target.value }); }}
                          aria-label={`Link ${v.plate ?? 'vehicle'} to a truck`}
                        >
                          <option value="">— link to a truck —</option>
                          {(hubVehicles ?? []).map((h) => (
                            <option key={h.id} value={h.id}>{h.plateEn} · {h.code}</option>
                          ))}
                        </select>
                        <Button size="sm" variant="outline" className="gap-1"
                          onClick={() => { setCreatingId(v.id); setNewPlate(hubPlateFrom(v.plate)); setErr(null); }}>
                          <Plus className="h-3.5 w-3.5" /> Create it
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot>
                <tr className="border-t border-border bg-muted/20 font-medium">
                  <td className="px-4 py-2">{rows.length} shown</td>
                  <td className="px-4 py-2"></td>
                  <td className="px-4 py-2 text-end font-mono">{num(totals.liters)}</td>
                  <td className="px-4 py-2 text-end font-mono">{sar(totals.sar)}</td>
                  <td className="px-4 py-2"></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        <p className="text-xs text-muted-foreground inline-flex items-start gap-2">
          <Truck className="h-3.5 w-3.5 shrink-0 mt-px" />
          Linking is remembered and never overwritten by a later sync. Link a truck once and every
          future fill goes straight into its history. One truck here can only take one Fleet Plus
          vehicle, so its diesel can never be counted twice.
        </p>
      </main>
    </div>
  );
}
